import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import RevealDropdown, {
  RevealDropdownTrigger
} from 'components/base/RevealDropdown';
import { Link } from 'react-router-dom';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { RootState, AppDispatch } from 'store';
import {
  fetchCompanies,
  deleteCompany,
  editCompany,
  Company
} from 'store/companySlice';
import ActionDropdownItems from 'components/common/ActionDropdownItems';
import EditCompanyModal from 'components/modals/EditCompanyModal';
import PhoenixLoader from 'components/common/PhoenixLoader';
import DeleteCompanyModal from 'components/modals/DeleteCompanyModal';
import ViewCompanyModal from 'components/modals/ViewCompanyModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBuilding,
  faCalendar,
  faCog
} from '@fortawesome/free-solid-svg-icons';

interface CompanyTableProps {
  searchQuery?: string;
}

const CompanyTable = ({ searchQuery = '' }: CompanyTableProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    data: companies,
    loading,
    error
  } = useSelector((state: RootState) => state.companies);

  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [openEditModal, setOpenEditModal] = useState(false);

  const [companyToDelete, setCompanyToDelete] = useState<Company | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const [viewCompany, setViewCompany] = useState<Company | null>(null);
  const [openViewModal, setOpenViewModal] = useState(false);

  useEffect(() => {
    dispatch(fetchCompanies());
  }, [dispatch]);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return companies;

    const query = searchQuery.toLowerCase().trim();
    return companies.filter(
      company =>
        company.name.toLowerCase().includes(query) ||
        company.id.toString().includes(query) ||
        company.created_at.toLowerCase().includes(query)
    );
  }, [companies, searchQuery]);

  const handleDeleteClick = (company: Company) => {
    setCompanyToDelete(company);
    setShowDeleteModal(true);
  };

  // ✅ Updated confirmDelete
  const confirmDelete = async () => {
    if (!companyToDelete) return;

    try {
      await dispatch(deleteCompany(companyToDelete.id)).unwrap();
      setShowDeleteModal(false);
      setCompanyToDelete(null);
    } catch (err) {
      console.error('Failed to delete company:', err);
      // Optional: show error inside the modal
    }
  };

  const handleEdit = (company: Company) => {
    setSelectedCompany(company);
    setOpenEditModal(true);
  };

  const handleSaveEdit = async (
    companyId: number,
    companyData: { name: string }
  ) => {
    try {
      const updatedCompany = await dispatch(
        editCompany({ id: companyId, companyData })
      ).unwrap();

      setSelectedCompany(updatedCompany);
      setOpenEditModal(false);
      setSelectedCompany(null);
    } catch (err) {
      console.error('Failed to update company', err);
    }
  };

  const handleView = (company: Company) => {
    setViewCompany(company);
    setOpenViewModal(true);
  };

  const table = useAdvanceTable({
    data: filteredData,
    columns: getColumns(handleView, handleEdit, handleDeleteClick),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <AdvanceTableProvider {...table}>
      <div
        className="bg-white"
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}
      >
        {loading ? (
          <PhoenixLoader />
        ) : error ? (
          <p className="p-4 text-danger">Error: {error}</p>
        ) : (
          <>
            <div className="table-responsive" style={{ overflowY: 'visible' }}>
              <AdvanceTable
                tableProps={{
                  className: 'phoenix-table mb-0',
                  hover: true
                }}
                headerClassName=""
                rowClassName="hover-actions-trigger btn-reveal-trigger align-middle"
              />
            </div>
            <div className="table-footer-elegant">
              <AdvanceTableFooter pagination className="px-3 py-2" />
            </div>
          </>
        )}
      </div>

      {openEditModal && selectedCompany && (
        <EditCompanyModal
          show={openEditModal}
          handleClose={() => {
            setOpenEditModal(false);
            setSelectedCompany(null);
          }}
          company={selectedCompany}
          onSave={handleSaveEdit}
        />
      )}

      {showDeleteModal && companyToDelete && (
        <DeleteCompanyModal
          show={showDeleteModal}
          companyName={companyToDelete.name}
          onClose={() => {
            setShowDeleteModal(false);
            setCompanyToDelete(null);
          }}
          onConfirm={confirmDelete}
        />
      )}

      {openViewModal && viewCompany && (
        <ViewCompanyModal
          show={openViewModal}
          handleClose={() => {
            setOpenViewModal(false);
            setViewCompany(null);
          }}
          company={viewCompany}
        />
      )}
    </AdvanceTableProvider>
  );
};

export default CompanyTable;

function getColumns(
  onView: (company: Company) => void,
  onEdit: (company: Company) => void,
  onDelete: (company: Company) => void
): ColumnDef<Company>[] {
  return [
    {
      id: 'sn',
      header: 'SN',
      cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
      meta: {
        headerProps: {
          style: { width: '80px' }
        },
        cellProps: {}
      }
    },
    {
      id: 'name',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
          Company Name
        </span>
      ),
      cell: ({ row: { original } }) => (
        <button
          className="btn btn-link p-0 fw-semibold text-decoration-none text-start"
          onClick={() => onView(original)}
        >
          {original.name}
        </button>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'created_at',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faCalendar} className="me-2 text-primary" />
          Created Date
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">
          {new Date(original.created_at).toLocaleDateString()}
        </span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      id: 'actions',
      header: () => (
        <div className="d-inline-flex align-items-center">
          <FontAwesomeIcon icon={faCog} className="me-2 text-primary" />
          Actions
        </div>
      ),
      cell: ({ row: { original } }) => (
        <RevealDropdownTrigger>
          <RevealDropdown>
            <ActionDropdownItems
              onView={() => onView(original)}
              onEdit={() => onEdit(original)}
              onRemove={() => onDelete(original)}
            />
          </RevealDropdown>
        </RevealDropdownTrigger>
      ),
      meta: {
        headerProps: { className: 'text-end pe-3' },
        cellProps: { className: 'text-end pe-3' }
      }
    }
  ];
}
